'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

interface ShowcaseItem {
  id: string;
  type: 'image' | 'video';
  label: string;
  style: string;
  beforeUrl: string;
  afterUrl: string;
}

const ITEMS: ShowcaseItem[] = [
  {
    id: 'ear-defenders-white',
    type: 'image',
    label: 'Ear Defenders',
    style: 'Clean White',
    beforeUrl: '/examples/before/ear-defenders.jpg',
    afterUrl: 'https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773811416506-kh45klhhqtm.png',
  },
  {
    id: 'leather-bag',
    type: 'image',
    label: 'Leather Bag',
    style: 'Home Scene',
    beforeUrl: '/examples/before/leather-bag.jpg',
    afterUrl: 'https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773811829194-sfnwf3lnwpf.png',
  },
  {
    id: 'boot',
    type: 'image',
    label: 'Boot',
    style: 'Desk & Table',
    beforeUrl: '/examples/before/boot.jpg',
    afterUrl: 'https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773812008840-b268no471qe.png',
  },
  {
    id: 'perfume-bestseller',
    type: 'image',
    label: 'Perfume Bottle',
    style: 'Best Seller',
    beforeUrl: '/examples/before/perfume-bottle.jpg',
    afterUrl: 'https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773819371121-kuwzeffao4.png',
  },
  {
    id: 'serum-luxury',
    type: 'image',
    label: 'Serum Bottles',
    style: 'Luxury',
    beforeUrl: '/examples/before/serum-bottles.jpg',
    afterUrl: 'https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773819206166-gj5fbbg4px.png',
  },
];

const SWEEP_MS = 3500;
const HOLD_MS = 1000;
const TOTAL_MS = SWEEP_MS + HOLD_MS;

export default function BeforeAfterShowcase() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [clipPercent, setClipPercent] = useState(100);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const inViewRef = useRef(false);

  const stopAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const animate = useCallback((ts: number) => {
    if (startRef.current === null) startRef.current = ts;
    const elapsed = ts - startRef.current;

    if (elapsed < SWEEP_MS) {
      setClipPercent(100 - (elapsed / SWEEP_MS) * 100);
      rafRef.current = requestAnimationFrame(animate);
    } else if (elapsed < TOTAL_MS) {
      setClipPercent(0);
      rafRef.current = requestAnimationFrame(animate);
    } else {
      startRef.current = null;
      setClipPercent(100);
      rafRef.current = requestAnimationFrame(animate);
    }
  }, []);

  const startAnimation = useCallback(() => {
    stopAnimation();
    startRef.current = null;
    setClipPercent(100);
    rafRef.current = requestAnimationFrame(animate);
  }, [animate, stopAnimation]);

  // Restart when active item changes (only if in view)
  useEffect(() => {
    if (inViewRef.current) startAnimation();
  }, [activeIdx, startAnimation]);

  // IntersectionObserver — start on enter, pause on leave
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          startAnimation();
        } else {
          stopAnimation();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [startAnimation, stopAnimation]);

  const item = ITEMS[activeIdx];
  const scanLeft = 100 - clipPercent;

  return (
    <section ref={sectionRef} id="conversion-examples" className="bg-[#0a0a0a] py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white">See the Transformation</h2>
            <p className="mt-3 text-gray-400">Real products. AI-enhanced visuals.</p>
          </div>

          {/* Main display */}
          <div
            className="relative mx-auto max-w-[900px] overflow-hidden rounded-2xl"
            style={{ aspectRatio: '4/3' }}
          >
            {/* Before layer */}
            <img
              src={item.beforeUrl}
              alt="Before"
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* After layer */}
            {item.type === 'video' ? (
              <video
                key={item.afterUrl}
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                style={{ clipPath: `inset(0 ${clipPercent}% 0 0)` }}
              >
                <source src={item.afterUrl} />
              </video>
            ) : (
              <img
                src={item.afterUrl}
                alt="After"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ clipPath: `inset(0 ${clipPercent}% 0 0)` }}
              />
            )}

            {/* Scan line */}
            <div
              className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
              style={{
                left: `${scanLeft}%`,
                background: 'white',
                boxShadow: '0 0 10px 4px rgba(255,255,255,0.6)',
              }}
            />

            {/* Labels */}
            <span className="absolute top-4 left-4 bg-blue-500 text-white text-sm font-semibold px-3 py-1 rounded-full select-none">
              Before
            </span>
            <span className="absolute top-4 right-4 bg-blue-500 text-white text-sm font-semibold px-3 py-1 rounded-full select-none">
              After
            </span>
          </div>

          {/* Thumbnail strip */}
          <div className="flex justify-center gap-4 mt-8 overflow-x-auto pb-2">
            {ITEMS.map((it, idx) => (
              <button
                key={it.id}
                onClick={() => setActiveIdx(idx)}
                className="flex-shrink-0 flex flex-col items-center gap-2"
              >
                <div
                  className={[
                    'w-20 h-20 rounded-xl overflow-hidden transition-opacity',
                    idx === activeIdx
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-black opacity-100'
                      : 'opacity-50 hover:opacity-80',
                  ].join(' ')}
                >
                  <img src={it.beforeUrl} alt={it.label} className="w-full h-full object-cover" />
                </div>
                <span className="text-xs text-gray-400">{it.style}</span>
              </button>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
